import React, { useState } from "react";
import Modal, { ModalProps } from "./Modal";
import { AlertTriangle } from "@/assets/Icons/AlertTriangle";
import Flex from "./Flex";
import { FormattedMessage, useLocale } from "@/i18n/i18n-client";
import Button from "@/templates/modern/components/Button/Button";
import useBreakPoints from "@/shared/utils/useBreakPoints";
import { useStore } from "@/lib/storeData/StoreContext";
import { useAuth } from "@/lib/Authentication/hooks/useAuth";
import { AuthMode } from "@/generated/graphql";
import CentralizedModal from "@/components/Centralizedlogin/CentralizedModal";
import { useRouter } from "@/i18n/i18n-navigation";
import { getPaymentLink } from "@/shared/utils/shinglePageCheckoutFunctions";
import { useCartData } from "@/lib/cartData/useCartData";

interface BuyNowConfirmModalProps extends ModalProps {
  productName: string;
  handleBuyNow: () => void;
  loading?: boolean;
}

const BuyNowConfirmModal = ({
  productName,
  handleBuyNow,
  loading,
  ...props
}: BuyNowConfirmModalProps) => {
  const { isTablet, isMobile } = useBreakPoints();
  const { authMode, handle, WuiltPayState } = useStore();
  const { user } = useAuth();
  const [isCentralizedModalOpen, setIsCentralizedModalOpen] = useState(false);
  const { code: localeCode } = useLocale();
  const router = useRouter();
  const token = user?.token;
  const { cart } = useCartData();

  const handleBuyNowClick = () => {
    handleBuyNow();
    if (authMode === AuthMode.Modern && !token) {
      setIsCentralizedModalOpen(true);
      return;
    } else {
      router.push(
        WuiltPayState?.enabled
          ? getPaymentLink(handle, cart?.id, user?.token, localeCode)
          : `/checkout/${cart?.id}`
      );
      props.onClose();
    }
  };

  return (
    <>
      <Modal
        onClose={props.onClose}
        show={props.show}
        modalWidth={isMobile ? "large" : isTablet ? "medium" : "small"}
        radius="12px"
      >
        <Modal.Body>
          <Flex column spacing="l">
            <div className="p-sm border-gray-200 border rounded-xl w-fit">
              <AlertTriangle />
            </div>
            <Flex column spacing="xs">
              <p className="m-0 font-semibold text-[18px] text-gray-900">
                <FormattedMessage defaultMessage="Cart reset" id="rZHXY3" />
              </p>
              <p className="m-0 text-gray-600 text-sm">
                <FormattedMessage
                  defaultMessage="You are about to delete all items in your cart and add <b>{productName}</b>"
                  id="DdjKIU"
                  values={{
                    productName,
                    b: (...chunks) => <b>{chunks}</b>,
                  }}
                />
              </p>
            </Flex>
            <Flex>
              <Button
                fullWidth
                type="secondary"
                onClick={props.onClose}
                borderRadius={8}
              >
                <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
              </Button>
              <Button
                fullWidth
                type="danger"
                onClick={handleBuyNowClick}
                borderRadius={8}
                isLoading={loading}
              >
                <FormattedMessage defaultMessage="Buy Now" id="lCILNz" />
              </Button>
            </Flex>
          </Flex>
        </Modal.Body>
      </Modal>
      {authMode === AuthMode.Modern && (
        <CentralizedModal
          isOpen={isCentralizedModalOpen}
          onClose={() => setIsCentralizedModalOpen(false)}
          onComplete={() => setIsCentralizedModalOpen(false)}
          onAuthSuccess={() => {
            // After successful authentication, proceed with buy now
            setIsCentralizedModalOpen(false);
            props.onClose(); // Close the confirm modal
            router.push(
              WuiltPayState?.enabled
                ? getPaymentLink(handle, cart?.id, user?.token, localeCode)
                : `/checkout/${cart?.id}`
            );
          }}
        />
      )}
    </>
  );
};

export default BuyNowConfirmModal;
